import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import { useFunctionalRef } from '@wix/da-hooks/pkg/useFunctionalRef';
import LazyLoadingImage from '../../LazyLoadingImage';
import AvatarPlaceholder from '../AvatarPlaceholder';
import s from './AvatarImage.scss';

const MAX_PIXELATED_RESOLUTION = 50;

function isSmallGifImage(img: HTMLImageElement) {
  if (!img.src) {
    return false;
  }
  let imgExtension = img.src.split('?').shift();
  if (!imgExtension) {
    return false;
  }

  imgExtension = imgExtension.split('.').pop();

  return (
    imgExtension === 'gif' &&
    img.naturalHeight <= MAX_PIXELATED_RESOLUTION &&
    img.naturalWidth <= MAX_PIXELATED_RESOLUTION
  );
}

export interface Props extends React.ImgHTMLAttributes<HTMLImageElement> {
  userId?: number;
  isGroup?: boolean;
  enableLazyLoad?: boolean;
}

const AvatarImage: React.FC<Props> = ({ isGroup, enableLazyLoad, userId, ...imgProps }) => {
  const [shouldBePixelated, setPixelated] = useState(false);
  const [imgRef, setRef] = useFunctionalRef<HTMLImageElement | null>(null);

  const onLoad = useCallback(() => {
    if (imgRef.current && isSmallGifImage(imgRef.current)) {
      setPixelated(true);
    }
  }, [imgRef]);

  const props: React.ImgHTMLAttributes<HTMLImageElement> = {
    ...imgProps,
    className: classnames(
      s['user-avatar'],
      shouldBePixelated && s['pixelated'],
      imgProps.className
    ),
    onLoad,
  };

  if (!imgProps.src) {
    return (
      <AvatarPlaceholder
        aria-label={imgProps.alt}
        isGroup={isGroup}
        userId={userId}
        width={imgProps.width}
        height={imgProps.height}
        className={imgProps.className}
        alt={imgProps.alt}
        aria-hidden={imgProps['aria-hidden']}
      />
    );
  }

  if (!enableLazyLoad) {
    return <img loading="lazy" {...props} />;
  }

  return <LazyLoadingImage innerRef={setRef} {...props} />;
};
AvatarImage.displayName = 'AvatarImage';

export default AvatarImage;
